import { useCallback, useMemo, useRef, useState } from 'react';
import DropDownButton from 'devextreme-react/drop-down-button';
import { GRID_LAYOUT_ACTIONS, buildLayoutMenuItems, getLayoutButtonText } from './grid-layout-menu';
import { GridLayoutNamePopup } from './grid-layout-name-popup';
import { LAYOUT_FILE_EXTENSION, isMatchingLayoutKey, parseLayoutImport } from './grid-layout-transfer';
import styles from './grid-layout-toolbar.module.scss';
import { NotifyFailure, NotifySuccess, saveTextAsFile } from '~/utils';

const DROP_DOWN_OPTIONS = { width: 260 };

// A file input cannot live inside a DropDownButton item, so one is kept hidden and clicked from the
// menu handler. Hidden with a style rather than type="hidden": a file input has to keep its type.
const HIDDEN_FILE_INPUT_STYLE = { display: 'none' };

// One popup serves three jobs, differing only in what it is called and what its button says.
const NAME_POPUP_TITLES = Object.freeze({
  saveAs: 'Save layout',
  rename: 'Rename layout',
  import: 'Import layout',
});

const NAME_POPUP_CONFIRM_TEXT = Object.freeze({
  saveAs: 'Save',
  rename: 'Rename',
  import: 'Import',
});

// FileReader rather than file.text(): jsdom's Blob has no text() in the version the tests run under,
// and FileReader is the older, universally available route to the same string.
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsText(file);
  });
}

// DropDownButton is imported from devextreme-react and used directly in JSX rather than referenced by
// a widget="dxDropDownButton" string, so no entry in devextreme-form-editors.js is needed: a directly
// used JSX binding survives the production build's tree-shaking, a string lookup would not.
export function GridLayoutToolbar({ gridLayout }) {
  // { mode: 'saveAs' | 'rename' | 'import', layoutId, initialName, imported } while the name popup is
  // up, else null.
  const [namePopup, setNamePopup] = useState(null);
  const fileInputRef = useRef(null);

  const {
    isEnabled,
    layoutKey,
    layouts,
    activeLayout,
    applyLayout,
    resetLayout,
    saveLayoutAs,
    updateActiveLayout,
    renameLayout,
    setDefaultLayout,
    deleteLayout,
    buildActiveLayoutExport,
    importLayout,
  } = gridLayout ?? {};

  const items = useMemo(
    () => buildLayoutMenuItems({ layouts, activeLayout }),
    [activeLayout, layouts]
  );

  // Every write below reaches the API through fetchApiOrThrow, which is deliberate so a duplicate
  // name or a lost row surfaces rather than failing quietly. The hook does not catch, so whatever
  // calls it has to. The name popup is the exception: its SaveButton needs the rejection to keep the
  // popup open, so those two paths are left to throw.
  const run = useCallback(async (operation, successMessage) => {
    try {
      await operation();
      if (successMessage) {
        NotifySuccess({ message: successMessage });
      }
    } catch (error) {
      NotifyFailure({ message: error?.message ?? 'That layout action could not be completed.' });
    }
  }, []);

  const handleItemClick = useCallback(
    async (e) => {
      const item = e?.itemData;
      // DevExtreme does honour `disabled` on a list item, but the menu is data-driven and a stray
      // click on a disabled action must not fall through to an operation with no active layout.
      if (!item || item.disabled) {
        return;
      }

      if (item.layoutId !== undefined) {
        await applyLayout(item.layoutId);
        return;
      }

      switch (item.action) {
        case GRID_LAYOUT_ACTIONS.SaveAs:
          setNamePopup({ mode: 'saveAs' });
          break;

        case GRID_LAYOUT_ACTIONS.Rename:
          // The id is captured now rather than read on submit, so a layout switch behind the popup
          // cannot redirect the rename onto a different layout.
          setNamePopup({ mode: 'rename', layoutId: activeLayout.id, initialName: activeLayout.name });
          break;

        case GRID_LAYOUT_ACTIONS.Update:
          await run(updateActiveLayout);
          break;

        case GRID_LAYOUT_ACTIONS.SetDefault:
          await run(
            () => setDefaultLayout(activeLayout.id, true),
            `"${activeLayout.name}" is now your default for this grid.`
          );
          break;

        case GRID_LAYOUT_ACTIONS.ClearDefault:
          await run(
            () => setDefaultLayout(activeLayout.id, false),
            `"${activeLayout.name}" is no longer your default for this grid.`
          );
          break;

        case GRID_LAYOUT_ACTIONS.Delete: {
          const { id, name } = activeLayout;
          if (!window.confirm(`Delete the saved layout "${name}"?`)) {
            return;
          }
          await run(() => deleteLayout(id), `Deleted layout "${name}".`);
          break;
        }

        case GRID_LAYOUT_ACTIONS.Export: {
          const { name } = activeLayout;
          await run(async () => {
            const { fileName, contents } = await buildActiveLayoutExport();
            saveTextAsFile(fileName, contents);
          }, `Exported "${name}".`);
          break;
        }

        case GRID_LAYOUT_ACTIONS.Import:
          // Nothing to await: the picker is the browser's, and the work resumes in handleFileChange.
          fileInputRef.current?.click();
          break;

        case GRID_LAYOUT_ACTIONS.Reset:
          // No notification: the grid visibly snapping back is the feedback.
          resetLayout();
          break;

        default:
          break;
      }
    },
    [
      activeLayout,
      applyLayout,
      buildActiveLayoutExport,
      deleteLayout,
      resetLayout,
      run,
      setDefaultLayout,
      updateActiveLayout,
    ]
  );

  // Reads the picked file, refuses what obviously cannot be imported, and hands the rest to the name
  // popup. The mismatch check here is only for speed — ImportGridLayoutCommand refuses it again, and
  // that refusal is the one that counts.
  const handleFileChange = useCallback(
    async (event) => {
      const input = event.target;
      const file = input.files?.[0];

      // Cleared straight away, and before any await: without it, picking the same file a second time
      // raises no change event, so a retry after a failed import would look like a dead menu item.
      input.value = '';
      if (!file) {
        return;
      }

      await run(async () => {
        const imported = parseLayoutImport(await readFileAsText(file));

        if (!isMatchingLayoutKey(layoutKey, imported.layoutKey)) {
          throw new Error('That layout was saved from a different grid and cannot be imported here.');
        }

        // The name is asked for rather than taken: a colleague's name may already be in use here, and
        // the popup is the only place that collision can be fixed without editing the file by hand.
        setNamePopup({ mode: 'import', initialName: imported.name, imported });
      });
    },
    [layoutKey, run]
  );

  const closeNamePopup = useCallback(() => setNamePopup(null), []);

  // Left to throw on purpose: the popup's SaveButton catches, notifies, and stays open so the user
  // can correct a duplicate name.
  const handleNameSubmit = useCallback(
    async (name) => {
      if (namePopup?.mode === 'rename') {
        await renameLayout(namePopup.layoutId, name);
        return;
      }

      if (namePopup?.mode === 'import') {
        const { imported } = namePopup;
        // The file's own key travels alongside this grid's, so the server can compare them itself.
        await importLayout(imported.layoutKey, name, imported.data);
        return;
      }

      await saveLayoutAs(name);
    },
    [importLayout, namePopup, renameLayout, saveLayoutAs]
  );

  const itemRender = useCallback(
    (item) => (
      <div className={[styles.item, item.beginGroup ? styles['item-group-start'] : ''].filter(Boolean).join(' ')}>
        {item.icon ? <i className={`dx-icon dx-icon-${item.icon} ${styles['item-icon']}`} /> : null}
        <span className={styles['item-text']}>{item.text}</span>
        {item.isDefault ? <i className={`dx-icon dx-icon-favorites ${styles['item-badge']}`} title="Your default" /> : null}
        {item.isActive ? <i className={`dx-icon dx-icon-check ${styles['item-badge']}`} title="Applied" /> : null}
      </div>
    ),
    []
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <DropDownButton
        text={getLayoutButtonText(activeLayout)}
        icon="detailslayout"
        stylingMode="text"
        items={items}
        keyExpr="key"
        displayExpr="text"
        useSelectMode={false}
        showArrowIcon={true}
        dropDownOptions={DROP_DOWN_OPTIONS}
        itemRender={itemRender}
        onItemClick={handleItemClick}
        elementAttr={{ 'data-testid': 'grid-layout-menu' }}
        hint="Saved layouts for this grid"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={`${LAYOUT_FILE_EXTENSION},text/plain`}
        style={HIDDEN_FILE_INPUT_STYLE}
        onChange={handleFileChange}
        data-testid="grid-layout-import-input"
      />
      {namePopup ? (
        <GridLayoutNamePopup
          visible={true}
          title={NAME_POPUP_TITLES[namePopup.mode] ?? NAME_POPUP_TITLES.saveAs}
          confirmText={NAME_POPUP_CONFIRM_TEXT[namePopup.mode] ?? NAME_POPUP_CONFIRM_TEXT.saveAs}
          initialName={namePopup.initialName ?? ''}
          onSubmit={handleNameSubmit}
          onClose={closeNamePopup}
        />
      ) : null}
    </>
  );
}
